/**
 * Every date and time the app writes.
 *
 * There used to be three formatters and they disagreed. `setup/schedule.ts` spelled its dates out
 * day-first (`1 Aug 04:00`) under a comment saying `toLocaleDateString` "would render `Aug 1` under
 * a US locale and disagree with every other date in the app" — while `cards/query.ts` was doing
 * exactly that, and the review-history tooltip was doing a third thing (`8/4/2026`). One of them
 * documented the others' behaviour as the thing to avoid.
 *
 * So: one formatter, units named on the value itself, locale-independent. Not a taste call — a due
 * date and a job's last run sit two clicks apart, and a reader should not have to work out which
 * convention each one is using.
 */

/**
 * `8月4日`. No leading zero — the day is read, not aligned.
 *
 * **Japanese, which reverses `docs/THEME.md`'s own exclusion list** — that names dates among the
 * things staying English, and the design canvases draw `9 Aug` throughout. Alex's call, 15 Aug
 * 2026, having read the built app on a phone: a date is the one piece of English left inside rows
 * that are otherwise entirely Japanese, and it reads as something the pass missed rather than as
 * something it kept.
 *
 * The ambiguity this module was written to remove is removed *harder* by the change rather than
 * reintroduced: `8月4日` names its own units, so there is no day-first/month-first convention left
 * to disagree about. That is why it is not `8/4`, which is exactly the form the docstring above
 * records three formatters getting wrong in three different ways.
 *
 * Not exported: `dayMonthMaybeYear` is the entry point, and a year-less variant on the public API
 * invites a future caller to pick it and drop the year from a date fourteen months out.
 */
function dayMonth(d: Date): string {
  return `${d.getMonth() + 1}月${d.getDate()}日`;
}

/**
 * `8月4日`, or `2027年8月4日` when it is not `now`'s year.
 *
 * The year is the exception, not the default: nearly every date on screen is this year, and four
 * more characters in a 74px column carry no information. But a card scheduled 14 months out read
 * `8月4日` and was indistinguishable from next week, which is the bug this closes.
 *
 * **Calendar year, not "within 12 months".** On 30 Dec, `4 Jan` is next week and still gets its
 * year, because the reader's unstated assumption is "this year" and that is what has to be
 * contradicted. A 12-month window would leave that date bare and mean something else entirely.
 */
export function dayMonthMaybeYear(d: Date, now: Date = new Date()): string {
  // The year LEADS in Japanese — `2027年8月4日` — where the English form appended it. Written the
  // other way round it is not merely foreign, it is unreadable: `8月4日2027` parses as a quantity.
  const year = d.getFullYear() === now.getFullYear() ? "" : `${d.getFullYear()}年`;
  return `${year}${dayMonth(d)}`;
}

/**
 * `04:00`. 24-hour, always — Read's gutter is 42px, exactly the five monospace characters this
 * returns, and a 12-hour clock's AM/PM wraps to a second line.
 *
 * Hand-padded rather than `toLocaleTimeString(…, { hour12: false })`, for the same reason
 * `dayMonth` is hand-written: that option is locale- and engine-dependent, and the `h23`/`h24`
 * hour-cycle distinction is exactly the sort of thing that renders midnight as `24:00` on some
 * stack and not another. This is the one format a 03:00 scheduled job's timestamp cannot get wrong.
 */
export function clock(d: Date): string {
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}
